import { generateRoundRobin } from './roundRobinGenerator';
import { ordinal } from './bracketUtils';
import { newId } from './idGen';

const standingPlaceholder = (rank) => ({
  id: `standing-${rank}`,
  name: `${ordinal(rank)} place`,
  isPlaceholder: true,
});

/**
 * Page Playoff (aka IPL Playoff, Double Chance Playoff): a round-robin
 * league — single round-robin by default, adjustable to double round-robin
 * (home-and-away, matching real IPL/Page-system convention) via the Legs
 * control on the generated schedule screen — followed by a fixed top-4
 * "double chance" ladder. This is the real, generic name for the structure
 * IPL borrowed from Australian sports (VFL/AFL, rugby league), not
 * something IPL invented.
 *
 * Seeds 1-2 get a second life if they lose their first playoff match (they
 * drop to Qualifier 2 instead of going out); seeds 3-4 face a straight
 * knockout in the Eliminator. Always exactly 4 qualifiers — unlike a normal
 * bracket, this ladder shape has no "N-qualifier" generalization.
 */
export const generatePagePlayoff = (participants, options = {}) => {
  const n = participants.length;
  if (n < 4) {
    throw new Error('Page Playoff needs at least 4 participants');
  }

  const legs = options.legs || 1;
  const leagueRounds = generateRoundRobin(participants, { legs });

  const qualifier1 = {
    id: newId(),
    number: 1,
    stage: 'Qualifier 1',
    slotA: standingPlaceholder(1),
    slotB: standingPlaceholder(2),
    note: 'Winner advances straight to the Final. Loser gets one more chance in Qualifier 2.',
  };
  const eliminator = {
    id: newId(),
    number: 2,
    stage: 'Eliminator',
    slotA: standingPlaceholder(3),
    slotB: standingPlaceholder(4),
    note: 'Loser is eliminated. Winner advances to Qualifier 2.',
  };
  const qualifier2 = {
    id: newId(),
    number: 3,
    stage: 'Qualifier 2',
    slotA: null,
    slotB: null,
    placeholderA: 'Loser of Qualifier 1',
    placeholderB: 'Winner of Eliminator',
    note: 'Winner advances to the Final.',
  };
  const final = {
    id: newId(),
    number: 4,
    stage: 'Final',
    slotA: null,
    slotB: null,
    placeholderA: 'Winner of Qualifier 1',
    placeholderB: 'Winner of Qualifier 2',
    note: 'Championship match.',
  };

  const playoff = [
    { id: newId(), round: 1, label: 'Qualifier 1 & Eliminator', matches: [qualifier1, eliminator] },
    { id: newId(), round: 2, label: 'Qualifier 2', matches: [qualifier2] },
    { id: newId(), round: 3, label: 'Final', matches: [final] },
  ];

  const executionOrder = [
    ...leagueRounds.map((r) => ({ ...r, label: `League — ${r.label}` })),
    ...playoff.map((r) => ({ ...r, round: leagueRounds.length + r.round, label: `Playoffs — ${r.label}` })),
  ];

  return {
    formatId: 'page_playoff',
    participantsCount: n,
    legs,
    leagueRounds,
    playoff,
    executionOrder,
  };
};
